import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { injectIntl } from 'react-intl';
import { useCSS } from '../../lib/hooks.js';
import { i18nIntl, isEmpty, isNotEmpty, staticImagesCdn } from '../../lib/ep.js';
import { useCanvasMachine } from '../../ts/games/canvasStateMachine.ts';

import GuessScoreCanvas from '../games/GuessScoreCanvas.js';
import { clientFinalPage } from '../games/StageFinalMessages.js';

import GuessScoreFinal from './GuessScoreFinal.js';
import GuessScoreResume from './GuessScoreResume.js';
import MisconfiguredGameWarning from './MisconfiguredGameWarning.js';
import SimpleCanvasContainer from './SimpleCanvasContainer.js';

import ActionButton from '../builder/ActionButton.js';
import ButtonCollection from '../builder/ButtonCollection.js';
import { localizeFields } from '../builder/DynamicFields.js';
import SaveButton from '../builder/SaveButton.js';

import LeveledHTMLText from './helpers/LeveledHTMLText.js';

export const getFullImage = (team, size = 'big') => {
	const applySize = (path_image, size) => {
		if(size == 'small') return path_image.replace(/([^\/]+)$/, "100-$1");
		return path_image;
	};
	if(isNotEmpty(team, 'full_image')) return applySize(team.full_image, size);
	if(team?.external_id ?? false) return applySize(`${staticImagesCdn(true)}teams/${String(team.external_id).slice(-2)}/${team.external_id}.png`,size);
	return false;
}

export const getTeamName = (team, length = 'all') => {
	if(['all', 'name'].includes(length)) {
		if(isNotEmpty(team, 'name')) return team.name;
		if(isNotEmpty(team, 'orig_name')) return team.orig_name;
	}
	
	if(['all', 'short_name'].includes(length)) {
		if(isNotEmpty(team, 'short_name')) return team.short_name;
		if(isNotEmpty(team, 'orig_short_name')) return team.orig_short_name;
	}

	if(['all'].includes(length)) return "-";

	return "";
};

export const getAvailableIntervals = (step, max, plain = true) => {
	const intervals = [];
	for(let i = 0; i < max - 1; i++) {
		const v = `${i * step + 1}-${(i + 1) * step}`;
		intervals.push((plain === false) ? { title:v } : v);
	}

	const v = `+${step * (max - 1)}`;
	intervals.push((plain === false) ? { title:v } : v);

	return intervals;
};

export const getFilteredMatches = (matches) => {
	return matches.filter((m) => {
		if(isEmpty(m, 'id_1')) return false;
		if(isEmpty(m, 'id_2')) return false;
		return true;
	});
};

export const getTeamInfoByID = (teams, id) => {
	return teams.find((team) => team.id == id);
};

export const getIntervalFromResults = (type, intervals, result_1, result_2) => {
	if(type != 'margin') return false;

	const difference = Math.abs(result_1 - result_2);

	let int = false;
	intervals.map((interval) => {
		if(interval.indexOf("+") > -1) {
			const final = parseInt(interval.substr(1));
			if(difference > final) {
				int = interval;
				return;
			}
		}

		const parts = interval.split("-");
		if(parseInt(parts[0]) <= difference && parseInt(parts[1]) >= difference) {
			int = interval;
			return;
		}
	});

	return int;
};

export const MatchDescription = forwardRef(({ ...props }, ref) => {
	const [ custom_description, setCustomDescription ] = useState("");

	const stage = props.stage.Stage ?? false;

	const common_description = (isNotEmpty(stage, 'main_description')) ? stage.main_description : "";

	useImperativeHandle(ref, () => ({
		updateCustomDescription(msg) {
			if(msg == undefined || msg == false || msg == "") {
				setCustomDescription("");
				return;
			}

			setCustomDescription(`<p>${msg}</p>`);
		},
	}), []);

	if(stage === false) return null;

	return (
		<LeveledHTMLText>{`${common_description}${custom_description}`}</LeveledHTMLText>
	)
});

const NavLinks = forwardRef(({ ...props }, ref) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const stage = props.stage.Stage ?? false;

	const type = stage?.guess_score_type ?? 'result';
	const is_builder = props?.is_builder ?? false;
	const is_test = props?.is_test ?? false;

	const [ actual_match, setActualMatch ] = useState(1);
	const [ is_changing, setIsChanging ] = useState(false);
	const [ is_next_disabled, setIsNextDisabled ] = useState((type == 'margin') ? true : false);

	const total_matches = props?.total_matches ?? 1;

	const goNextMatch = () => {
		if(is_builder && !is_test) return;

		setActualMatch(actual_match + 1);
		setIsChanging(true);
		props.gameCanvas.current.navigateToMatch('next', () => {
			setIsChanging(false);
		});
	};

	const goPrevMatch = () => {
		if(is_builder && !is_test) return;

		setActualMatch(actual_match - 1);
		setIsChanging(true);
		props.gameCanvas.current.navigateToMatch('prev', () => {
			setIsChanging(false);
		});
	};

	const goFinishGame = (cb) => {
		if(is_builder && !is_test) return;

		props.gameCanvas.current.finishGame((total_matches < 2) ? false : true, cb);
	};

	useImperativeHandle(ref, () => ({
		enableNext() {
			if(type != 'margin') return;
			setIsNextDisabled(false);
		},

		disableNext() {
			if(type != 'margin') return;
			setIsNextDisabled(true);
		},
	}), []);

	if(stage === false) return null;

	if(total_matches < 2) {
		return (
			<ButtonCollection
				_class={{ other:'flex_button gui_big_button guess_score_buttons' }}
			>
				<SaveButton
					_fullclass=''
					icon='gui_nav_link in_top'
					onClick={(e, cb) => goFinishGame(cb)}
					style={{ justifyContent:'center' }}
					disabled={(is_changing) ? true : false}
					autotranslate={false}
				>{i18n("***Finish")}</SaveButton>
			</ButtonCollection>
		);
	}

	return (
		<ButtonCollection
			_class={{ other:'flex_button guess_score_buttons' }}
		>
			<ActionButton
				_fullclass=''
				icon='gui_nav_link in_top'
				onClick={goPrevMatch}
				disabled={(actual_match == 1 || is_changing) ? true : false}
				autotranslate={false}
			>{i18n("previous", 'ucfirst')}</ActionButton>

			<span style={{ flexGrow:'1' }}>{actual_match} / {total_matches}</span>

			{(actual_match < total_matches) ? (
				<ActionButton
					_fullclass=''
					icon='gui_nav_link in_top'
					onClick={goNextMatch}
					disabled={(is_changing || is_next_disabled) ? true : false}
					autotranslate={false}
				>{i18n("next", 'ucfirst')}</ActionButton>
			) : (
				<ActionButton
					_fullclass=''
					icon='gui_nav_link in_top'
					onClick={goFinishGame}
					disabled={(is_changing || is_next_disabled) ? true : false}
					autotranslate={false}
				>{i18n("***Finish")}</ActionButton>
			)}
		</ButtonCollection>
	)
});

const GuessScoreClient = ({ stage, ...props }) => {
	useCSS('comp/guess_score.css');

	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const [ is_reseting, setIsReseting ] = useState(false);

	const is_builder = props?.is_builder ?? false;
	const is_test = props?.is_test ?? false;

	const filtered_matches = getFilteredMatches(JSON.parse(JSON.stringify(stage?.Stage?.guess_score_matches ?? [])));

	const nav_ref = useRef({});
	const header_ref = useRef({});
	const reset_iteration = useRef(stage.Stage?.reset ?? -2);

	const promotion = props?.promotion?.Promotion ?? {};

	const teams = localizeFields((promotion?.guess_score_teams ?? []), props.locale);

	useEffect(() => {
		if(!is_builder && !is_test) return;
		if(reset_iteration.current == -2) {
			reset_iteration.current = -1;
			return;
		}

		const new_iteration = stage.Stage?.reset ?? 0;
		setIsReseting(true);
		reset_iteration.current = parseInt(new_iteration);

		setTimeout(() => {
			setIsReseting(false);
		}, 100);
	}, [ stage.Stage.reset ]);

	const findTeamById = (id) => {
		return teams.find((t) => (t.id == id));
	};

	const canvas_images = () => {
		const images = [];

		if(isNotEmpty(stage.Stage, 'bg_image')) {
			images.push({
				id:'bg_image',
				name:stage.Stage.bg_image,
				url:stage.Stage.full_bg_image,
				fit:'cover',
			})
		}

		filtered_matches.map((match) => {
			const team_1 = findTeamById(match.id_1);
			const team_2 = findTeamById(match.id_2);

			if(team_1 != undefined && (isNotEmpty(team_1, 'image') || isNotEmpty(team_1, 'external_id'))) {
				images.push({
					id:`logo_${team_1.id}`,
					name:(isNotEmpty(team_1, 'short_name')) ? team_1.short_name : team_1.name,
					url:getFullImage(team_1),
					fit:'contain',
					get_average_color:true,
				});
			}

			if(team_2 != undefined && (isNotEmpty(team_2, 'image') || isNotEmpty(team_2, 'external_id'))) {
				images.push({
					id:`logo_${team_2.id}`,
					name:(isNotEmpty(team_2, 'short_name')) ? team_2.short_name : team_2.name,
					url:getFullImage(team_2),
					fit:'contain',
					get_average_color:true,
				});
			}
		});

		return images;
	};

	const [ state, dispatch ] = useCanvasMachine({
		images:canvas_images,
		images_dependencies:[
			stage.Stage.bg_image,
			stage.Stage.guess_score_matches,
		],
	});

	const default_props = {
		indexed_images:state.images,
		loaded_status:state.state,
		locale:props?.locale,
	};
	
	if(state.state != 'LOADED') return null;
	if(is_reseting) return null;

	const min_teams = 2;

	let missing_anything = false;
	let missing_anything_text = "";
	if(teams.length < min_teams) {
		missing_anything = 'teams';
		missing_anything_text = props.intl.formatMessage({ id:"Create at least {num} teams" }, { num:min_teams });
	} else if(filtered_matches.length < 1) {
		missing_anything = 'matches';
		missing_anything_text = i18n("Create at least 1 match");
	}

	const final_stage = clientFinalPage(stage, props.actual_state);

	const default_bg_color = promotion?.layout_props?.color?.background_color ?? 'rgba(255, 255, 255, 1)';
	const default_text_color = promotion?.layout_props?.color?.text_color ?? 'rgba(0, 0, 0, 1)';
	const default_main_color = promotion?.layout_props?.color?.main_color ?? 'rgba(30, 34, 170, 1)';

	const default_description_text = `<h1>${i18n("Predict the results")}</h1><p>${i18n("Make your prediction for each match.")}</p>`;
	if(isEmpty(final_stage.Stage, 'main_description')) final_stage.Stage.main_description = default_description_text;
	if(isEmpty(final_stage.Stage, 'bg_color')) final_stage.Stage.bg_color = default_bg_color;
	if(isEmpty(final_stage.Stage, 'text_color')) final_stage.Stage.text_color = default_text_color;
	if(isEmpty(final_stage.Stage, 'main_color')) final_stage.Stage.main_color = default_main_color;
	if(isNotEmpty(final_stage.Stage, 'guess_score_step_2_instructions') && isEmpty(final_stage.Stage, 'guess_score_step_1_instructions')) {
		final_stage.Stage.guess_score_step_1_instructions = " ";
	}

	return (
		<SimpleCanvasContainer
			{...props}
			stage={final_stage}
			header_image={final_stage.Stage.full_guess_score_sponsors_image}
			final_page={<GuessScoreFinal
				{...default_props}
				stats={props?.stats ?? {}}
			/>}
			resume_page={<GuessScoreResume
				{...default_props}
				is_builder={is_builder}
				is_test={is_test}
			/>}
			custom_description={<MatchDescription
				ref={header_ref}
				stage={stage}
			/>}
			extra_content={<NavLinks
				ref={nav_ref}
				stage={stage}
				total_matches={filtered_matches.length}
				is_builder={is_builder}
				is_test={is_test}
				intl={props.intl}
			/>}
			game_type='guess_score'
			force_game_to_square={(missing_anything) ? true : false}
		>
			{(missing_anything === false) ? (
				<GuessScoreCanvas
					{...default_props}
					teams={teams}
					matches={filtered_matches}
					buttons={nav_ref}
					header={header_ref}
					//step_1_instructions={i18n("Select the winner...")}
					//step_2_instructions={i18n("... now select the score difference")}
				/>
			) : (
				<MisconfiguredGameWarning
					message={missing_anything_text}
					autotranslate={false}
				/>
			)}
		</SimpleCanvasContainer>
	)
};

export default injectIntl(GuessScoreClient);